import React, { useContext, useState } from 'react';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import '../pages/Settings.scss';
import './WikiRebuildSettings.scss';

type RebuildStats = {
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
};

const DEFAULT_ERROR_MESSAGE = 'שגיאה בבניית האינדקס מחדש. אנא נסה שנית.';

// Super-admin only. Triggers POST /api/protocol-wiki/rebuild — see
// docs/protocols/Protocol-Wiki.md for the endpoint contract and skip semantics.
// Replaces the old apps/scripts/wiki-rebuild/* CLI scripts: no machine login needed.
const WikiRebuildSettings: React.FC = () => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = Boolean(user && user.isSuperAdmin);

  const [force, setForce] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [stats, setStats] = useState<RebuildStats | null>(null);
  const [error, setError] = useState<string>('');

  if (!isSuperAdmin) {
    return null;
  }

  const handleRebuild = async () => {
    setIsRunning(true);
    setError('');
    setStats(null);

    try {
      // The rebuild can take minutes (a Claude call per protocol) — no timeout,
      // and handle errors locally so a failure is always visible in this panel.
      const response = await API.post<RebuildStats>(
        '/api/protocol-wiki/rebuild',
        { force },
        { timeout: 0, handlingErrorLocally: true }
      );
      setStats(response.data);
    } catch (err: unknown) {
      console.error('Protocol wiki rebuild failed:', err);
      const apiError = err as { response?: { data?: { error?: { hebMessage?: string; message?: string } } } };
      setError(apiError.response?.data?.error?.hebMessage
        || apiError.response?.data?.error?.message
        || DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="settings-section wiki-rebuild-settings">
      <h2 className="section-title">אינדקס פרוטוקולים (AI)</h2>
      <p className="wiki-rebuild-description">
        בונה מחדש את קטלוג הפרוטוקולים עבור הסייען החכם (סיכומים ודפי אינדקס לכל מחלקה).
        הפעולה יכולה להימשך מספר דקות.
      </p>

      <label className="wiki-rebuild-force-option">
        <input
          type="checkbox"
          checked={force}
          onChange={(e) => setForce(e.target.checked)}
          disabled={isRunning}
        />
        צור מחדש את הכל (כולל פרוטוקולים שכבר מעודכנים)
      </label>

      <button
        type="button"
        className="button-primary"
        onClick={handleRebuild}
        disabled={isRunning}
      >
        {isRunning ? 'בונה מחדש...' : 'בנה מחדש את אינדקס הפרוטוקולים'}
      </button>

      {error && <div className="error-message">{error}</div>}

      {stats && (
        <div className={stats.failed > 0 ? 'error-message' : 'success-message'}>
          <div>
            הבנייה מחדש הושלמה: {stats.processed} פרוטוקולים נבדקו,
            {' '}{stats.succeeded} עודכנו בהצלחה, {stats.skipped} דולגו (כבר מעודכנים),
            {' '}{stats.failed} נכשלו.
          </div>
          {stats.failed > 0 && (
            <div>לחיצה נוספת על הכפתור (ללא הסימון למעלה) תנסה שוב רק את הפרוטוקולים שנכשלו.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default WikiRebuildSettings;
